import { Injectable } from '@angular/core';
import { sortBy } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastService } from 'src/app/common/service/toast.service';
import { FilterListFieldEntry } from '../model/filter-list-field-entry';
import { FilterListFieldType } from '../model/filter-list-field-type';
import { FilterDataService } from './filter-data-service';

@Injectable()
export class FilterDataProvider {
  private loading = new BehaviorSubject<FilterDataLoadingInfo[]>([]);
  private data = new BehaviorSubject<FilterData[]>([]);

  constructor(private filterDataService: FilterDataService, private toastService: ToastService) { }

  getFieldAvailableValues(fieldType: FilterListFieldType): Observable<FilterListFieldEntry[]> {
    this.initializeDataSource(fieldType);
    return this.data.asObservable().pipe(
      map(v => v.find(c => c.fieldType === fieldType)?.data ?? [])
    );
  }

  getLoadingState(fieldType: FilterListFieldType): Observable<boolean> {
    return this.loading.asObservable().pipe(
      map(v => v.find(c => c.fieldType === fieldType)?.loading ?? false)
    );
  }

  updateDataForField(fieldType: FilterListFieldType): void {
    this.initializeDataSource(fieldType);
  }

  private initializeDataSource(fieldType: FilterListFieldType): void {
    const alreadyLoading = this.loading.getValue().some(c => c.fieldType === fieldType && c.loading);
    if (alreadyLoading) return;

    this.setLoading(fieldType, true);
    this.filterDataService.fetchFilterListFieldAvailableValues(fieldType)
      .pipe(
        map(filterData => sortBy(filterData, data => data.displayValue))
      )
      .subscribe({
        next: filterData => {
          this.setData(fieldType, filterData);
          this.setLoading(fieldType, false);
        },
        error: error => {
          console.error('Failed to fetch filter field available values', error);
          this.toastService.showErrorToast('fetchFilterFieldAvailableValuesError');
          this.setData(fieldType, []);
          this.setLoading(fieldType, false);
        }
      });
  }

  private setLoading(fieldType: FilterListFieldType, loading: boolean): void {
    const others = this.loading.getValue().filter(v => v.fieldType !== fieldType);
    this.loading.next([...others, { fieldType, loading }]);
  }

  private setData(fieldType: FilterListFieldType, data: FilterListFieldEntry[]): void {
    const others = this.data.getValue().filter(v => v.fieldType !== fieldType);
    this.data.next([...others, { fieldType, data }]);
  }

}

export interface FilterDataLoadingInfo {
  fieldType: FilterListFieldType;
  loading: boolean;
}

export interface FilterData {
  fieldType: FilterListFieldType;
  data: FilterListFieldEntry[];
}
